
import Vue from 'vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import Checkbox from './Checkbox.vue'
import Input from '@/components/Input.vue'

type Input = Vue & { hasError: boolean; hasFocused: boolean }

@Component<MultiCheckBox>({
    components: {
        Checkbox,
        Input,
    },
})
export default class MultiCheckBox extends Vue {
    @Prop({ type: Array, default: () => [] }) public selected!: any[]
    @Prop({ type: Boolean, default: false }) public selectAll!: boolean
    @Prop({ type: Object, default: () => ({}) }) public options!: any
    @Prop({ type: Object, default: () => ({}) }) public optionsSuffix!: any
    @Prop({ type: Boolean, default: false }) public hasSearchBar!: boolean
    @Prop({ type: String, default: '' }) public title!: string
    @Prop({ type: String, default: '' }) public header!: string
    @Prop({ type: Boolean, default: false }) public add!: boolean
    @Prop({ type: Boolean, default: false }) public open!: boolean

    public adding = false
    public newData?: any = null
    public newDataAdded = false
    public opened = false
    public searchText = ''

    public mounted() {
        this.opened = this.open
    }

    public get optionsObj() {
        if (Array.isArray(this.options)) {
            const opt = {}
            this.options.forEach((v) => {
                opt[v] = v
            })
            return opt
        } else {
            return this.options
        }
    }

    public get getSortedOptions() {
        const options = {}

        Object.keys(this.optionsObj)
            .map((key) => ({ k: key, v: this.optionsObj[key] }))
            // handle search
            .filter(
                (item) =>
                    this.searchText.length === 0 ||
                    item.k
                        .toLocaleLowerCase()
                        .indexOf(this.searchText.toLowerCase()) >= 0
            )
            .sort((option1, option2) => (option1.v > option2.v ? 1 : -1))
            .forEach((option) => (options[option.k] = option.v))
        return options
    }

    public get selectedCount(): number {
        if (this.selected) {
            return this.selected.length
        } else {
            return 0
        }
    }

    public get ids() {
        return Object.keys(this.optionsObj)
    }

    public get totalCount(): number {
        return this.ids.length
    }

    public get partialChecked(): boolean {
        return this.selectedCount > 0 && this.selectedCount < this.totalCount
    }

    public get allChecked(): boolean {
        return this.totalCount > 0 && this.selectedCount === this.totalCount
    }

    // new regex to validate data
    public get regex() {
        if (!this.totalCount) {
            return null
        } else {
            if (
                this.newData &&
                Object.values(this.optionsObj).indexOf(this.newData) > -1
            ) {
                return new RegExp('^$') // make it invalid
            } else {
                return null
            }
        }
    }

    public getOptionLabel(k) {
        return `${k} ${
            this.optionsSuffix && this.optionsSuffix[k]
                ? this.optionsSuffix[k]
                : ''
        }`
    }

    public toggleCheckAll() {
        if (this.allChecked) {
            this.resetCheckboxes()
        } else {
            this.setCheckboxes()
        }
    }

    public resetCheckboxes() {
        const newSelected = []
        if (newSelected.length !== this.selectedCount) {
            this.$emit('toggle', newSelected)
        }
    }

    public setCheckboxes() {
        const newSelected = this.ids
        if (newSelected.length !== this.selectedCount) {
            this.$emit('toggle', newSelected)
        }
    }

    public toggleCheck(id) {
        const newSelected = this.selected as any[]
        if (newSelected.includes(id)) {
            const i = newSelected.indexOf(id)
            newSelected.splice(i, 1)
            this.$emit('toggle', newSelected)
        } else {
            newSelected.push(id)
            this.$emit('toggle', newSelected)
        }
    }

    public shouldCheck(id): boolean {
        if (!this.selectedCount) {
            return false
        } else {
            const curr = this.selected as any[]
            return curr.includes(id)
        }
    }

    public onAddData() {
        const inputEl = this.$refs.newDataInput as Input
        if (this.newData && !inputEl.hasError) {
            this.$emit('add', this.newData)
            // select new data by default
            this.toggleCheck(this.newData)
            this.newData = null
            this.adding = false
            this.newDataAdded = true

            setTimeout(() => {
                this.newDataAdded = false
            }, 1000)
        }
    }
}
